import { json, redirect } from "@remix-run/node";
import {
  Form,
  useActionData,
  useLoaderData,
  useTransition,
} from "@remix-run/react";
import type { ActionArgs, LoaderArgs } from "@remix-run/server-runtime";
import { ApiError } from "openapi-typescript-fetch";
import { Trans, useTranslation } from "react-i18next";
import invariant from "tiny-invariant";
import { Button } from "~/components/Button";
import { CountrySelect } from "~/components/CountrySelect";
import { InputWithLabel } from "~/components/Input";
import { Link } from "~/components/Link";
import { ExternalLink } from "~/components/Link/ExternalLink";
import { BodyMedium } from "~/components/Typography";
import { createRegistrationRequest } from "~/services/api/auth.server";
import { getApiInformation } from "~/services/api/information.server";
import { authenticator } from "~/services/auth.server";
import { getSession, sessionStorage } from "~/services/session.server";
import { parseErrMsg } from "~/utils/parseErrMsg";

export const action = async ({ request }: ActionArgs) => {
  const form = await request.formData();

  const displayName = form.get("displayName");
  const email = form.get("email");
  const password = form.get("password");
  const countryCode = form.get("countryCode");

  invariant(typeof displayName === "string");
  invariant(typeof email === "string");
  invariant(typeof password === "string");
  invariant(typeof countryCode === "string");

  try {
    await createRegistrationRequest(request, {
      displayName,
      email,
      password,
      countryCode,
    });
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        ok: false,
        message: parseErrMsg(err),
      };
    }
    throw err;
  }

  const session = await getSession(request);
  session.set("signup_email", email);

  throw redirect("/signup/mfa", {
    headers: {
      "Set-Cookie": await sessionStorage.commitSession(session),
    },
  });
};

export const loader = async ({ request }: LoaderArgs) => {
  await authenticator.isAuthenticated(request, { successRedirect: "/" });

  const info = await getApiInformation(request);

  const ipCountry = request.headers.get("CF-IPCountry");

  return json({
    information: info,
    initCountryCode: ipCountry,
  });
};

export default function SignUpIndex() {
  const { t } = useTranslation();

  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const transition = useTransition();

  const isLoading =
    transition.type === "actionSubmission" ||
    transition.type === "actionRedirect";

  return (
    <div className="mx-auto flex flex-col items-center">
      <div>
        <header className="mb-3 text-[32px] font-bold leading-[52px] text-gray-800">
          {t("webapp.sign_up.title", "Create your account")}
        </header>
        <BodyMedium className="mb-5 font-semibold text-gray-600">
          {t(
            "webapp.sign_up.description",
            "Please fill in the following fields"
          )}
        </BodyMedium>
        <div className="text-[14px] font-semibold leading-[22px] text-gray-400">
          {t("webapp.sign_up.heading", "Already have an account?")}{" "}
          <Link to="/login" variants="primary">
            {t("webapp.sign_up.login_link", "Login")}.
          </Link>
        </div>
        <Form className="mt-5 flex flex-col gap-5" method="post">
          <InputWithLabel
            label={t("webapp.name", "Name")}
            autoFocus
            id="displayName"
            type="text"
            name="displayName"
            autoComplete="name"
            placeholder={t("webapp.enter_your_name", "Enter your name")}
            minLength={2}
            maxLength={16}
            required
          />
          <InputWithLabel
            label={t("webapp.email", "Email")}
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            placeholder={t("webapp.enter_your_email", "Enter your email")}
            required
          />
          <InputWithLabel
            label={t("webapp.password", "Password")}
            id="password"
            type="password"
            name="password"
            autoComplete="new-password"
            minLength={6}
            maxLength={32}
            required
          />
          <div>
            <label
              className="text-body-md mb-[6px] block font-semibold text-gray-500"
              htmlFor="countryCode"
            >
              {t("webapp.where_do_you_live", "Where do you live?")}
            </label>
            <CountrySelect
              initialValue={loaderData.initCountryCode}
              prohibitedCountryCodes={
                loaderData.information.prohibited_country_codes
              }
            />
          </div>
          {actionData?.message && (
            <p className="typesetting-validation-error">{actionData.message}</p>
          )}
          <div className="text-sm leading-normal text-gray-600">
            <Trans i18nKey="webapp.disclaimer_text.sign_up">
              By tapping "Create Account", you agree to our{" "}
              <ExternalLink
                className="text-sm leading-normal"
                to="https://finblox.com/terms-of-use"
              >
                Terms of Use
              </ExternalLink>{" "}
              and confirm that you have read our{" "}
              <ExternalLink
                className="text-sm leading-normal"
                to="https://finblox.com/risk-disclosure"
              >
                Risk Disclosure
              </ExternalLink>
              ,{" "}
              <ExternalLink
                className="text-sm leading-normal"
                to="https://finblox.com/privacy-policy"
              >
                Privacy Policy
              </ExternalLink>
              , and{" "}
              <ExternalLink
                className="text-sm leading-normal"
                to="https://help.finblox.com/en/articles/6344319-important-disclaimer"
              >
                Disclaimer
              </ExternalLink>
              .
            </Trans>
          </div>
          <Button loading={isLoading} className="mt-1">
            {t("webapp.create_account", "Create Account")}
          </Button>
        </Form>
      </div>
    </div>
  );
}
